/**
 * ErrorState - User Foundation Component
 * PRD-USER-001: Mobile-First Error State
 * 
 * Mobile-optimized error display with large touch targets.
 * Wraps analytics and humanization logic.
 */

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { ERROR_TYPES, ErrorType, reportUxError, trackErrorRetry } from '@/lib/analytics/error-telemetry';
import { cn } from '@/lib/utils';
import { UX_LEXICON, humanizeError } from '@/lib/ux-lexicon';
import { semanticTokens } from '@packages/config/theme';
import * as React from 'react';

export interface ErrorStateProps {
  /** Error title */
  title?: string;
  /** Error message */
  message?: string;
  /** Retry callback */
  onRetry?: () => void;
  /** Retry button label */
  retryLabel?: string;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Visual variant */
  variant?: 'default' | 'destructive';
  /** Error type for analytics */
  errorType?: ErrorType | string;
  /** Surface where error is shown */
  surface?: string;
  /** Critical error flag */
  critical?: boolean;
  /** Raw error object */
  error?: unknown;
  /** Secondary action */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'outline' | 'ghost' | 'destructive';
  };
  className?: string;
}

export function ErrorState({
  title,
  message,
  onRetry,
  retryLabel = 'Retry',
  icon,
  className,
  variant = 'destructive',
  errorType = ERROR_TYPES.UNKNOWN_ERROR,
  surface = 'unknown',
  critical = false,
  error,
  secondaryAction,
}: ErrorStateProps) {
  // Humanize error
  const humanized = React.useMemo(() => {
    if (error) return humanizeError(error);
    return null;
  }, [error]);

  const displayTitle = title || humanized?.title || UX_LEXICON.errors.unknown.title;
  const displayMessage = message || humanized?.message || UX_LEXICON.errors.unknown.message;
  const normalizedErrorType = (errorType || ERROR_TYPES.UNKNOWN_ERROR) as ErrorType;

  // Track error
  React.useEffect(() => {
    reportUxError(error || new Error(displayMessage), {
      type: normalizedErrorType,
      surface,
      critical,
      metadata: error ? { error_message: displayMessage } : undefined,
    });
  }, [error, normalizedErrorType, surface, critical, displayMessage]);

  const handleRetry = React.useCallback(() => {
    trackErrorRetry(normalizedErrorType, surface);
    onRetry?.();
  }, [onRetry, normalizedErrorType, surface]);

  return (
    <div
      className={cn('py-6 px-4', semanticTokens.motion.standard, className)}
      role="alert"
      aria-live="assertive"
    >
      <Alert variant={variant} className="border-none bg-transparent p-0">
        <AlertDescription className="flex flex-col items-center text-center space-y-4">
          {/* Icon */}
          <div
            className={cn(
              'w-12 h-12 rounded-full flex items-center justify-center mb-2',
              variant === 'destructive' ? semanticTokens.danger.bg : semanticTokens.bg.muted
            )}
          >
            {icon || (
              <SFIcon
                icon={sfIcons.warning}
                className={cn(
                  'h-6 w-6',
                  variant === 'destructive' ? semanticTokens.danger.accent : semanticTokens.fg.muted
                )}
                aria-hidden="true"
              />
            )}
          </div>

          {/* Content */}
          <div className="space-y-2 max-w-xs">
            <h3 className={semanticTokens.typography.h4}>
              {displayTitle}
            </h3>
            <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
              {displayMessage}
            </p>
          </div>

          {/* Actions - Mobile Optimized (Full width on small screens) */}
          {(onRetry || secondaryAction) && (
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mt-4">
              {onRetry && (
                <Button
                  variant="outline"
                  size="lg" // Large for touch targets
                  onClick={handleRetry}
                  className={cn(
                    'w-full sm:w-auto min-h-[44px] gap-2',
                    semanticTokens.motion.standard,
                    semanticTokens.focus.ring
                  )}
                >
                  <SFIcon icon={sfIcons.refresh} className="h-4 w-4" aria-hidden="true" />
                  {retryLabel}
                </Button>
              )}
              {secondaryAction && (
                <Button
                  variant={secondaryAction.variant || 'ghost'}
                  size="lg"
                  onClick={secondaryAction.onClick}
                  className={cn(
                    'w-full sm:w-auto min-h-[44px]',
                    semanticTokens.motion.standard,
                    semanticTokens.focus.ring
                  )}
                >
                  {secondaryAction.label}
                </Button>
              )}
            </div>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
}

